/**
 * "Roles or access needed" as the Bluestar area picker (8 Oct, product owner).
 *
 * Shown instead of the free text when the form's system is 365FO_BS / Bluestar
 * (`bluestarRolesParameterId`): the areas of the Master approval matrix with
 * View or Design, the global roles / add-ons, and "Other (type it)" — the
 * access wizard's `RolePicker`, titled as the form's question. The answer
 * stays the text Ivanti gets (`areasText`), so nothing downstream changes.
 * Until the list has loaded, or if it cannot be read, the form's own text box
 * is shown instead.
 */

import type { ReactNode } from "react";

import { RolePicker } from "@/features/access/components/role-picker";
import { useT } from "@/i18n";
import { logger } from "@/lib/logger";

import { useRoleLists } from "../api/queries";
import { areasText, BLUESTAR_LIST_KEY, parseAreasText } from "../lib/bluestar-areas";

export interface BluestarAreasFieldProps {
  name: string;
  /** The form's question ("Roles or access needed"). */
  label: string;
  /** The form's own help text and the field's note ("from your message"), after ours. */
  notes?: readonly (string | null | undefined)[];
  required: boolean;
  error?: string;
  value: string | null;
  onChange: (text: string) => void;
  /** The plain text box, while the list loads or when it cannot be read. */
  fallback: ReactNode;
}

export function BluestarAreasField({
  name,
  label,
  notes = [],
  required,
  error,
  value,
  onChange,
  fallback,
}: BluestarAreasFieldProps) {
  const { t } = useT("tickets");
  const lists = useRoleLists();
  const list = lists.data?.role_lists?.find((entry) => entry.key === BLUESTAR_LIST_KEY);
  if (list === undefined) {
    if (lists.isError) {
      logger.warn("[tickets] Bluestar areas unavailable; the free-text field is shown");
    }
    return <>{fallback}</>;
  }
  return (
    <RolePicker
      name={name}
      list={list}
      title={label}
      required={required}
      error={error}
      hint={[t("create.bluestarAreas.hint"), ...notes].filter(Boolean).join(" ")}
      value={parseAreasText(list, value)}
      onChange={(next) => onChange(areasText(list, next))}
    />
  );
}
